import React, { useState, useMemo } from 'react';
import { useDroppable, useDndContext } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Column, Task } from '../../../types';
import { useKanbanStore } from '../../../store/useKanbanStore';
import { ColumnFooter } from './ColumnFooter';
import { TaskCard } from '../../tasks/components/TaskCard';

interface KanbanColumnProps {
  column: Column;
  tasks: Task[];
  onTaskClick?: (task: Task) => void;
  onAddTaskClick?: (columnId: string) => void;
  canEditTasks?: boolean;
}

export const KanbanColumn: React.FC<KanbanColumnProps> = ({
  column,
  tasks,
  onTaskClick,
  onAddTaskClick,
  canEditTasks = true,
}) => {
  const store = useKanbanStore() as any;
  const deleteColumn = store.deleteColumn;
  const updateColumn = store.updateColumn;
  const toggleSubtask = store.toggleSubtask;
  const setSelectedTask = store.setSelectedTask || store.openTaskModal;
  
  // 1. Pull the view settings and column reordering tools from the store
  const showTime = store.showTime;
  const showCost = store.showCost;
  const getActiveProjectColumns = store.getActiveProjectColumns;
  const moveColumn = store.moveColumn;

  // Compute column position for reordering controls
  const activeColumns = typeof getActiveProjectColumns === 'function' ? getActiveProjectColumns() : [];
  const currentIndex = activeColumns.findIndex((c: Column) => c.id === column.id);

  // Grab the global drag state from dnd-kit
  const { active } = useDndContext();
  const isDragging = active !== null;

  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleText, setTitleText] = useState(column.title);
  const [isSettingWipLimit, setIsSettingWipLimit] = useState(false);
  const [wipLimitInput, setWipLimitInput] = useState(column.wipLimit || 0);

  // Register column area as a droppable target
  const { setNodeRef } = useDroppable({
    id: column.id,
    data: {
      type: 'COLUMN',
      column,
    },
  });

  const taskIdKey = tasks.map((t) => t.id).join('\0');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const taskIds = useMemo(() => tasks.map((t) => t.id), [taskIdKey]);

  const handleTitleSubmit = () => {
    if (titleText.trim() && titleText !== column.title) {
      if (typeof updateColumn === 'function') {
        updateColumn(column.id, { title: titleText.trim() });
      }
    } else {
      setTitleText(column.title);
    }
    setIsEditingTitle(false);
  };

  const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = e.target.value;
    if (typeof updateColumn === 'function') {
      updateColumn(column.id, { color: newColor });
    }
  };

  const handleDeleteColumn = () => {
    const confirmed = window.confirm(
      `Are you sure you want to delete column "${column.title}"? All tasks inside will also be removed.`
    );
    if (confirmed) {
      deleteColumn?.(column.id);
    }
  };

  const handleCardClick = (task: Task) => {
    if (typeof onTaskClick === 'function') {
      onTaskClick(task);
    } else if (typeof setSelectedTask === 'function') {
      setSelectedTask(task);
    }
  };

  const handleCreateTask = () => {
    if (typeof onAddTaskClick === 'function') {
      onAddTaskClick(column.id);
    } else if (typeof store.openTaskModal === 'function') {
      store.openTaskModal(column.id);
    }
  };

  const currentColor = column.color || '#3b82f6';
  const wipLimit = column.wipLimit || 0;
  const isWipLimitReached = wipLimit > 0 && tasks.length >= wipLimit;
  const handleWipLimitSave = () => {
    const nextLimit = Math.max(0, Math.min(999, Math.floor(Number(wipLimitInput) || 0)));
    setWipLimitInput(nextLimit);
    updateColumn?.(column.id, { wipLimit: nextLimit });
    setIsSettingWipLimit(false);
  };

  return (
    <div
      ref={setNodeRef}
      className={`flex flex-col w-72 sm:w-80 min-w-[18rem] sm:min-w-[20rem] shrink-0 bg-slate-100/80 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-3 max-h-full transition-colors ${isWipLimitReached ? 'ring-2 ring-red-400/50' : ''}`} title={isWipLimitReached ? 'WIP limit reached' : undefined}
    >
      {/* Column Header */}
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="flex items-center gap-1.5 flex-1 mr-2">
          {canEditTasks && <>
          {/* Move Column Left Button */}
          <button
            type="button"
            disabled={currentIndex <= 0}
            onClick={() => moveColumn?.(column.id, currentIndex - 1)}
            className="p-0.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 disabled:opacity-20 disabled:hover:text-slate-400 transition-colors"
            title="Move column left"
          >
            ◀
          </button>

          {/* Interactive Color Indicator */}
          <div className="relative group shrink-0" title="Click to change LED indicator color">
            <span
              className="block w-3.5 h-3.5 rounded-full cursor-pointer shadow-sm transition-transform group-hover:scale-125"
              style={{ backgroundColor: currentColor }}
            />
            <input
              type="color"
              value={currentColor}
              onChange={handleColorChange}
              className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
            />
          </div>

          {isEditingTitle ? (
            <input
              type="text"
              value={titleText}
              onChange={(e) => setTitleText(e.target.value)}
              onBlur={handleTitleSubmit}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleTitleSubmit();
                if (e.key === 'Escape') {
                  setTitleText(column.title);
                  setIsEditingTitle(false);
                }
              }}
              autoFocus
              className="text-sm font-semibold text-slate-800 dark:text-slate-100 bg-white dark:bg-slate-800 border border-blue-400 dark:border-blue-500 rounded px-1.5 py-0.5 w-full focus:outline-none shadow-sm"
            />
          ) : (
            <h2
              onClick={() => setIsEditingTitle(true)}
              title="Click to rename column"
              className="font-semibold text-slate-800 dark:text-slate-100 text-sm cursor-pointer hover:bg-slate-200/60 dark:hover:bg-slate-800 px-1.5 py-0.5 rounded transition-colors truncate"
            >
              {column.title}
            </h2>
          )}

          {/* Move Column Right Button */}
          <button
            type="button"
            disabled={currentIndex === -1 || currentIndex >= activeColumns.length - 1}
            onClick={() => moveColumn?.(column.id, currentIndex + 1)}
            className="p-0.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 disabled:opacity-20 disabled:hover:text-slate-400 transition-colors"
            title="Move column right"
          >
            ▶
          </button>

          </>}
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${isWipLimitReached ? 'bg-red-600 text-white' : 'bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-400'}`}>
            {wipLimit > 0 ? `${tasks.length}/${wipLimit}${isWipLimitReached ? ' 🔒' : ''}` : tasks.length}
          </span>
          {canEditTasks && (
            <div className="relative shrink-0">
              <button
                type="button"
                onClick={() => {
                  setWipLimitInput(wipLimit);
                  setIsSettingWipLimit((open) => !open);
                }}
                className="p-1 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 rounded transition-colors"
                title="Set WIP limit"
                aria-label={`Set WIP limit for ${column.title}`}
              >
                ⚙️
              </button>
              {isSettingWipLimit && (
                <div className="absolute right-0 top-7 z-20 flex items-center gap-1 rounded-md border border-slate-200 bg-white p-1.5 shadow-lg dark:border-slate-700 dark:bg-slate-800">
                  <label htmlFor={`wip-limit-${column.id}`} className="sr-only">WIP limit</label>
                  <input
                    id={`wip-limit-${column.id}`}
                    type="number"
                    min="0"
                    max="999"
                    value={wipLimitInput}
                    onChange={(e) => setWipLimitInput(Math.max(0, Math.min(999, Number(e.target.value) || 0)))}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleWipLimitSave(); if (e.key === 'Escape') setIsSettingWipLimit(false); }}
                    className="w-16 rounded border border-slate-300 bg-white px-1.5 py-1 text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={handleWipLimitSave}
                    className="rounded bg-blue-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-blue-700"
                  >
                    Save
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Column Delete Button */}
        {canEditTasks && <button
          onClick={handleDeleteColumn}
          className="text-slate-400 hover:text-rose-600 dark:hover:text-rose-400 text-xs transition-colors p-1 rounded hover:bg-slate-200/60 dark:hover:bg-slate-800 shrink-0"
          title="Delete Column"
        >
          🗑️
        </button>}
      </div>

      {/* Task Cards List */}
      <div 
        className={`flex-1 overflow-y-auto overflow-x-hidden pr-1 space-y-2.5 min-h-[100px] ${
          isDragging ? 'scrollbar-hide' : 'scrollbar-thin'
        }`}
      >
        <SortableContext items={taskIds} strategy={verticalListSortingStrategy}>
          {tasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              onToggleSubtask={(taskId, subtaskId) => {
                if (typeof toggleSubtask === 'function') {
                  toggleSubtask(taskId, subtaskId);
                }
              }}
              onClick={handleCardClick}
              canEditTasks={canEditTasks}
            />
          ))}
        </SortableContext>
      </div>

      {/* Add Task Button */}
      {canEditTasks && <button
        onClick={handleCreateTask}
        className="mt-3 w-full py-2 px-3 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200/80 dark:hover:bg-slate-800 rounded-lg transition-colors flex items-center justify-center gap-1 border border-dashed border-slate-300 dark:border-slate-700"
      >
        <span>+ Add Task</span>
      </button>}

      {/* Time & Cost Summary Card */}
      {(showTime || showCost) && (
        <ColumnFooter tasks={tasks} />
      )}
    </div>
  );
};